import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, MessageCircleQuestion, RefreshCcw, Hotel } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner } from "../components/common";
import { HPHeader } from "../components/hotelspulse/HPHeader";

const Row = ({ k, v }) => v ? (
  <div className="flex justify-between gap-4 py-2 border-b border-zinc-100 text-sm">
    <span className="text-zinc-500">{k}</span><span className="font-600 text-right">{v}</span>
  </div>
) : null;

export default function HotelsPulseQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(false);
  const [msg, setMsg] = useState("");
  const [responded, setResponded] = useState(null);

  useEffect(() => {
    api.get(`/directstay/quotes/${token}`).then((r) => setQ(r.data.quote)).catch(() => setErr(true));
  }, [token]);

  const respond = async (type) => {
    if (type !== "accept" && !msg.trim()) { toast.error("Please add a message"); return; }
    try {
      await api.post(`/directstay/quotes/${token}/respond`, { type, message: msg });
      setResponded(type);
    } catch { toast.error("Could not send response"); }
  };

  if (err) return <div className="max-w-xl mx-auto px-5 py-24 text-center font-heading font-900 text-2xl">Quote not found or expired</div>;
  if (!q) return <Spinner full />;

  const expired = q.status === "expired";

  return (
    <div data-testid="quote-page" className="bg-zinc-50 min-h-screen">
      <HPHeader />
      <div className="max-w-xl mx-auto px-5 py-10">
        <div className="bg-emerald-950 text-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-400 flex items-center gap-2"><Hotel className="w-4 h-4" /> Direct quote via HotelsPulse</div>
          <h1 className="font-heading font-900 text-2xl mt-2">{q.property_name}</h1>
          <p className="text-emerald-200 text-sm mt-1">Prepared for {q.traveler_name}{q.contact_person && ` · from ${q.contact_person}`}</p>
        </div>
        <div className="bg-white border border-zinc-300 border-t-0 p-6">
          <Row k="Room" v={q.room} />
          <Row k="Dates" v={q.check_in && `${q.check_in} → ${q.check_out}`} />
          <Row k="Guests" v={q.guests} />
          <Row k="Nightly rate" v={<span className="text-emerald-700 font-900">{q.nightly_rate} {q.currency !== "INR" ? q.currency : ""}</span>} />
          <Row k="Taxes" v={q.taxes} />
          <Row k="Meals included" v={q.meals_included} />
          <Row k="Cancellation" v={q.cancellation_policy} />
          <Row k="Payment" v={q.payment_instructions} />
          <Row k="Quote valid until" v={q.expires_at ? new Date(q.expires_at).toLocaleDateString() : null} />
          {q.benefits?.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 p-3">
              <div className="text-xs font-700 uppercase text-amber-700 mb-1">Direct-booking benefits</div>
              <ul className="text-sm space-y-1">{q.benefits.map((b) => <li key={b} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {b}</li>)}</ul>
            </div>
          )}

          {responded || ["accepted", "question", "change_requested"].includes(q.status) ? (
            <div data-testid="quote-responded" className="mt-6 border-2 border-emerald-600 bg-emerald-50 p-4 text-center">
              <div className="font-heading font-900 text-emerald-800">
                {(responded || q.status) === "accepted" || responded === "accept" ? "Quote accepted — the property will contact you to confirm." : "Response sent to the property."}
              </div>
              {q.booking_url && <a href={q.booking_url} target="_blank" rel="noreferrer" className="inline-block mt-3 bg-emerald-700 text-white font-700 px-5 py-2.5 text-sm uppercase">Continue to hotel's booking page</a>}
            </div>
          ) : expired ? (
            <div className="mt-6 border border-zinc-300 bg-zinc-50 p-4 text-center text-zinc-500">This quote has expired. Contact the property for an updated rate.</div>
          ) : (
            <div className="mt-6 space-y-3">
              <button data-testid="quote-accept" onClick={() => respond("accept")} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-700 py-3 text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Accept this quote
              </button>
              <textarea data-testid="quote-message" rows={2} className="w-full border border-zinc-300 px-3 py-2 text-sm" placeholder="Ask a question or request a change…" value={msg} onChange={(e) => setMsg(e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="quote-question" onClick={() => respond("question")} className="border border-zinc-300 py-2.5 text-sm font-600 inline-flex items-center justify-center gap-2 hover:border-emerald-600"><MessageCircleQuestion className="w-4 h-4" /> Ask a question</button>
                <button data-testid="quote-change" onClick={() => respond("change_request")} className="border border-zinc-300 py-2.5 text-sm font-600 inline-flex items-center justify-center gap-2 hover:border-emerald-600"><RefreshCcw className="w-4 h-4" /> Request a change</button>
              </div>
            </div>
          )}
          <p className="text-[11px] text-zinc-400 mt-6">Payment is handled directly by the property through its own secure booking process. HotelsPulse does not collect reservation payments and charges no commission on your stay.</p>
        </div>
      </div>
    </div>
  );
}
